/**
 * Standard API Response Types and Error Handling
 * Provides consistent error response format across the application
 */

export interface BaseApiResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

export interface ApiSuccessResponse<T = unknown> extends BaseApiResponse {
  success: true;
  data: T;
  meta?: {
    pagination?: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
    search_time_ms?: number;
    filters?: Record<string, unknown>;
  };
}

export interface ApiErrorResponse extends BaseApiResponse {
  success: false;
  error: {
    code: string;
    type: ErrorType;
    message: string;
    message_en?: string;
    details?: string | Record<string, unknown>;
    field_errors?: FieldError[];
    request_id?: string;
    timestamp: string;
  };
  debug_info?: {
    stack_trace?: string;
    endpoint?: string;
    method?: string;
    params?: Record<string, unknown>;
  };
}

export type ApiResponse<T = unknown> = ApiSuccessResponse<T> | ApiErrorResponse;

// Error Types
export enum ErrorType {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  DATABASE_ERROR = 'DATABASE_ERROR',
  BUSINESS_LOGIC_ERROR = 'BUSINESS_LOGIC_ERROR'
}

// Error Codes
export enum ErrorCode {
  // Authentication & Authorization
  INVALID_CREDENTIALS = 'AUTH_INVALID_CREDENTIALS',
  TOKEN_EXPIRED = 'AUTH_TOKEN_EXPIRED',
  TOKEN_INVALID = 'AUTH_TOKEN_INVALID',
  INSUFFICIENT_PERMISSIONS = 'AUTH_INSUFFICIENT_PERMISSIONS',
  ACCOUNT_DISABLED = 'AUTH_ACCOUNT_DISABLED',
  EMAIL_NOT_VERIFIED = 'AUTH_EMAIL_NOT_VERIFIED',

  // Validation
  REQUIRED_FIELD_MISSING = 'VALIDATION_REQUIRED_FIELD',
  INVALID_FORMAT = 'VALIDATION_INVALID_FORMAT',
  VALUE_TOO_LONG = 'VALIDATION_VALUE_TOO_LONG',
  VALUE_TOO_SHORT = 'VALIDATION_VALUE_TOO_SHORT',
  INVALID_EMAIL = 'VALIDATION_INVALID_EMAIL',
  INVALID_PHONE = 'VALIDATION_INVALID_PHONE',
  PASSWORD_TOO_WEAK = 'VALIDATION_PASSWORD_TOO_WEAK',
  DUPLICATE_VALUE = 'VALIDATION_DUPLICATE_VALUE',

  // Business Logic
  COMPANY_NOT_FOUND = 'BUSINESS_COMPANY_NOT_FOUND',
  COMPANY_INACTIVE = 'BUSINESS_COMPANY_INACTIVE',
  SEARCH_NO_RESULTS = 'BUSINESS_SEARCH_NO_RESULTS',
  OPERATION_NOT_ALLOWED = 'BUSINESS_OPERATION_NOT_ALLOWED',

  // System
  RATE_LIMIT_EXCEEDED = 'SYSTEM_RATE_LIMIT_EXCEEDED',
  SERVICE_UNAVAILABLE = 'SYSTEM_SERVICE_UNAVAILABLE',
  DATABASE_CONNECTION_ERROR = 'SYSTEM_DATABASE_ERROR',
  EXTERNAL_SERVICE_ERROR = 'SYSTEM_EXTERNAL_SERVICE_ERROR',

  // Generic
  INTERNAL_SERVER_ERROR = 'SYSTEM_INTERNAL_ERROR',
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// Field-level validation errors
export interface FieldError {
  field: string;
  code: string;
  message: string;
  message_en?: string;
  value?: unknown;
}

// Error context for debugging
export interface ErrorContext {
  endpoint?: string;
  method?: string;
  user_id?: string;
  request_id?: string;
  client_ip?: string;
  user_agent?: string;
  timestamp?: string;
}

// Error messages in Thai and English
export const ERROR_MESSAGES = {
  [ErrorCode.INVALID_CREDENTIALS]: {
    th: 'อีเมลหรือรหัสผ่านไม่ถูกต้อง',
    en: 'Invalid email or password'
  },
  [ErrorCode.TOKEN_EXPIRED]: {
    th: 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่',
    en: 'Session expired, please login again'
  },
  [ErrorCode.TOKEN_INVALID]: {
    th: 'เซสชันไม่ถูกต้อง กรุณาเข้าสู่ระบบใหม่',
    en: 'Invalid session, please login again'
  },
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: {
    th: 'คุณไม่มีสิทธิ์ในการดำเนินการนี้',
    en: 'You do not have permission to perform this action'
  },
  [ErrorCode.ACCOUNT_DISABLED]: {
    th: 'บัญชีถูกปิดการใช้งาน',
    en: 'Account is disabled'
  },
  [ErrorCode.EMAIL_NOT_VERIFIED]: {
    th: 'กรุณายืนยันอีเมลก่อนใช้งาน',
    en: 'Please verify your email before using the service'
  },
  [ErrorCode.REQUIRED_FIELD_MISSING]: {
    th: 'กรุณากรอกข้อมูลให้ครบถ้วน',
    en: 'Required field is missing'
  },
  [ErrorCode.INVALID_FORMAT]: {
    th: 'รูปแบบข้อมูลไม่ถูกต้อง',
    en: 'Invalid data format'
  },
  [ErrorCode.INVALID_EMAIL]: {
    th: 'รูปแบบอีเมลไม่ถูกต้อง',
    en: 'Invalid email format'
  },
  [ErrorCode.INVALID_PHONE]: {
    th: 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง',
    en: 'Invalid phone number format'
  },
  [ErrorCode.PASSWORD_TOO_WEAK]: {
    th: 'รหัสผ่านต้องมีความปลอดภัยมากกว่านี้',
    en: 'Password is too weak'
  },
  [ErrorCode.DUPLICATE_VALUE]: {
    th: 'ข้อมูลนี้มีการใช้งานแล้ว',
    en: 'This value is already in use'
  },
  [ErrorCode.COMPANY_NOT_FOUND]: {
    th: 'ไม่พบบริษัทที่ค้นหา',
    en: 'Company not found'
  },
  [ErrorCode.COMPANY_INACTIVE]: {
    th: 'บริษัทนี้ไม่ได้ใช้งานแล้ว',
    en: 'Company is inactive'
  },
  [ErrorCode.SEARCH_NO_RESULTS]: {
    th: 'ไม่พบผลการค้นหา',
    en: 'No search results found'
  },
  [ErrorCode.OPERATION_NOT_ALLOWED]: {
    th: 'ไม่สามารถดำเนินการได้ในขณะนี้',
    en: 'Operation not allowed at this time'
  },
  [ErrorCode.RATE_LIMIT_EXCEEDED]: {
    th: 'คุณส่งคำร้องบ่อยเกินไป กรุณารอสักครู่',
    en: 'Too many requests, please wait a moment'
  },
  [ErrorCode.SERVICE_UNAVAILABLE]: {
    th: 'บริการไม่สามารถใช้งานได้ในขณะนี้',
    en: 'Service is currently unavailable'
  },
  [ErrorCode.DATABASE_CONNECTION_ERROR]: {
    th: 'เกิดปัญหาในการเชื่อมต่อฐานข้อมูล',
    en: 'Database connection error'
  },
  [ErrorCode.EXTERNAL_SERVICE_ERROR]: {
    th: 'เกิดปัญหาในการเชื่อมต่อบริการภายนอก',
    en: 'External service error'
  },
  [ErrorCode.INTERNAL_SERVER_ERROR]: {
    th: 'เกิดข้อผิดพลาดภายในเซิร์ฟเวอร์',
    en: 'Internal server error'
  },
  [ErrorCode.NETWORK_TIMEOUT]: {
    th: 'การเชื่อมต่อหมดเวลา กรุณาลองใหม่',
    en: 'Network timeout, please try again'
  },
  [ErrorCode.UNKNOWN_ERROR]: {
    th: 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
    en: 'Unknown error occurred'
  }
} as const;

// HTTP Status Code mappings
export const ERROR_STATUS_MAPPINGS = {
  [ErrorType.VALIDATION_ERROR]: 400,
  [ErrorType.AUTHENTICATION_ERROR]: 401,
  [ErrorType.AUTHORIZATION_ERROR]: 403,
  [ErrorType.NOT_FOUND_ERROR]: 404,
  [ErrorType.RATE_LIMIT_ERROR]: 429,
  [ErrorType.SERVER_ERROR]: 500,
  [ErrorType.NETWORK_ERROR]: 502,
  [ErrorType.TIMEOUT_ERROR]: 504,
  [ErrorType.DATABASE_ERROR]: 503,
  [ErrorType.BUSINESS_LOGIC_ERROR]: 422
} as const;

// Utility type guards
export function isApiErrorResponse(response: unknown): response is ApiErrorResponse {
  return typeof response === 'object' && response !== null && 'success' in response && !response.success && 'error' in response;
}

export function isApiSuccessResponse<T>(response: unknown): response is ApiSuccessResponse<T> {
  return typeof response === 'object' && response !== null && 'success' in response && response.success && 'data' in response;
}

// Error creation utilities
export function createErrorResponse(
  code: ErrorCode,
  type: ErrorType,
  details?: string | Record<string, unknown>,
  fieldErrors?: FieldError[],
  context?: ErrorContext
): ApiErrorResponse {
  const errorMessage = ERROR_MESSAGES[code];

  return {
    success: false,
    message: errorMessage?.th || 'เกิดข้อผิดพลาด',
    timestamp: new Date().toISOString(),
    error: {
      code,
      type,
      message: errorMessage?.th || 'เกิดข้อผิดพลาด',
      message_en: errorMessage?.en || 'An error occurred',
      details,
      field_errors: fieldErrors,
      request_id: context?.request_id,
      timestamp: new Date().toISOString()
    }
  };
}

export function createFieldError(
  field: string,
  code: string,
  message: string,
  messageEn?: string,
  value?: unknown
): FieldError {
  return {
    field,
    code,
    message,
    message_en: messageEn,
    value
  };
}